"use client";

import { Container } from "@/components/ui/container";
import { Reveal } from "@/components/ui/reveal";
import { Icon } from "@/components/ui/icon";
import { solution } from "@/lib/data/homepage";
import { EngineLoop, usePulse } from "./visuals";

/**
 * The Solution — animated 9-engine system diagram (left) + 4 solution
 * pillars as floating glass cards (right). Asymmetric.
 */
export function Solution() {
  const pulse = usePulse();
  return (
    <section id="solution" className="relative section-pad">
      <Container>
        <div className="grid lg:grid-cols-[0.85fr_1.15fr] gap-10 items-center">
          {/* LEFT — system diagram */}
          <Reveal>
            <div className="relative">
              <div className="rounded-3xl glass p-8 relative overflow-hidden">
                <div className="absolute -top-20 -left-20 h-64 w-64 rounded-full bg-[var(--color-purple)] opacity-15 blur-3xl pointer-events-none" />
                <div className="eyebrow-tiny text-[var(--color-purple)] mb-2">
                  9 Engines, One Platform
                </div>
                <h3 className="text-xl font-bold leading-tight mb-6">
                  The closed-loop system that turns data into narrative dominance.
                </h3>
                <div className="flex justify-center">
                  <EngineLoop />
                </div>
                <div className="mt-6 grid grid-cols-3 gap-2 text-[0.7rem]">
                  <div className="rounded-lg border border-[rgb(var(--border)/0.15)] p-2 text-center">
                    <div className="font-mono-num font-bold" style={{ color: "var(--color-cyan)" }}>OSE</div>
                    <div className="text-[rgb(var(--fg-subtle))] mt-0.5">Organic Sim</div>
                  </div>
                  <div className="rounded-lg border border-[rgb(var(--border)/0.15)] p-2 text-center">
                    <div className="font-mono-num font-bold" style={{ color: "var(--color-crimson)" }}>CRE</div>
                    <div className="text-[rgb(var(--fg-subtle))] mt-0.5">Cog. Resonance</div>
                  </div>
                  <div className="rounded-lg border border-[rgb(var(--border)/0.15)] p-2 text-center">
                    <div className="font-mono-num font-bold" style={{ color: "var(--color-amber)" }}>ECA</div>
                    <div className="text-[rgb(var(--fg-subtle))] mt-0.5">Echo Chamber</div>
                  </div>
                </div>
              </div>
            </div>
          </Reveal>

          {/* RIGHT — solution narrative + 4 pillars */}
          <Reveal delay={120}>
            <div className="space-y-8">
              <div>
                <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass eyebrow-tiny text-[var(--color-emerald)]">
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-emerald)]" />
                  The Solution
                </span>
                <h2 className="mt-5 text-[clamp(2.2rem,5vw,3.6rem)] font-bold leading-[1.04] tracking-[-0.025em]">
                  {solution.title}
                </h2>
                <p className="mt-5 text-body leading-[1.7] text-[rgb(var(--fg-muted))] max-w-xl">
                  {solution.intro}
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {solution.pillars.map((p, i) => (
                  <div
                    key={p.title}
                    className="group rounded-2xl border border-[rgb(var(--border)/0.18)] bg-[rgb(var(--surface)/0.5)] hover:bg-[rgb(var(--surface)/0.8)] p-4 transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <span
                        className="grid place-items-center h-9 w-9 rounded-lg shrink-0 transition-transform group-hover:scale-110"
                        style={{
                          background: `linear-gradient(135deg, color-mix(in oklch, ${i === 0 ? 'var(--color-cyan)' : i === 1 ? 'var(--color-purple)' : i === 2 ? 'var(--color-emerald)' : 'var(--color-amber)'} 22%, transparent))`,
                          boxShadow: `inset 0 0 0 1px color-mix(in oklch, ${i === 0 ? 'var(--color-cyan)' : i === 1 ? 'var(--color-purple)' : i === 2 ? 'var(--color-emerald)' : 'var(--color-amber)'} 35%, transparent)`,
                        }}
                      >
                        <Icon name={p.icon} className="h-4 w-4" style={{ color: i === 0 ? 'var(--color-cyan)' : i === 1 ? 'var(--color-purple)' : i === 2 ? 'var(--color-emerald)' : 'var(--color-amber)' }} />
                      </span>
                      <div>
                        <h3>{p.title}</h3>
                        <p className="mt-1.5 text-caption leading-relaxed text-[rgb(var(--fg-muted))]">
                          {p.desc}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </Container>
    </section>
  );
}
